import React, { useRef, useState } from 'react'
import { GenerativeAIUploadFileResponse } from 'shared'
import { useToast } from '@chakra-ui/react'

interface UseAIFileUploadProps {
  uploadUrl?: string
}

interface UseAIFileUploadReturn {
  uploadedFiles: GenerativeAIUploadFileResponse[]
  fileIsUploading: boolean
  fileInputRef: React.RefObject<HTMLInputElement>
  handleUpload: (event: React.ChangeEvent<HTMLInputElement>) => Promise<void>
  removeFile: (file: GenerativeAIUploadFileResponse) => void
  clearFiles: () => void
}

export const useAIFileUpload = ({
  uploadUrl = '/api/ai/rich-text/file',
}: UseAIFileUploadProps = {}): UseAIFileUploadReturn => {
  const [uploadedFiles, setUploadedFiles] = useState<GenerativeAIUploadFileResponse[]>([])
  const [fileIsUploading, setFileIsUploading] = useState(false)
  const fileInputRef = useRef<HTMLInputElement>(null)
  const toast = useToast()

  const handleUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files
    if (!files || files.length === 0) return

    setFileIsUploading(true)
    const formData = new FormData()

    // Append all files to the form data
    for (let i = 0; i < files.length; i++) {
      formData.append(`file${i}`, files[i])
    }

    try {
      const response = await fetch(uploadUrl, {
        method: 'POST',
        body: formData,
      })

      if (!response.ok) {
        console.error('File upload failed:', response.statusText)
        toast({
          title: 'Nepodařilo se nahrát soubory',
          status: 'error',
          duration: 5000,
          isClosable: true,
          position: 'top',
        })
        return
      }

      const responseJson = await response.json()

      // Handle both single file and multiple files responses
      if (Array.isArray(responseJson)) {
        setUploadedFiles((currentFiles) => [...currentFiles, ...responseJson])
        toast({
          title: `${responseJson.length} souborů nahráno`,
          status: 'success',
        })
      } else {
        setUploadedFiles((currentFiles) => [...currentFiles, responseJson])
        toast({
          title: 'Soubor nahrán',
          status: 'success',
        })
      }
    } catch (error) {
      console.error('File upload error:', error)
      toast({
        title: 'Chyba při nahrávání souborů',
        status: 'error',
        duration: 5000,
        isClosable: true,
      })
    } finally {
      if (fileInputRef.current) {
        fileInputRef.current.value = ''
      }
      setFileIsUploading(false)
    }
  }

  const removeFile = (file: GenerativeAIUploadFileResponse) => {
    if (window.confirm('Skutečně chcete odstranit soubor?')) {
      setUploadedFiles((files) => files.filter((f) => f.file.uri !== file.file.uri))
    }
  }

  const clearFiles = () => {
    setUploadedFiles([])
    if (fileInputRef.current) {
      fileInputRef.current.value = ''
    }
  }

  return {
    uploadedFiles,
    fileIsUploading,
    fileInputRef,
    handleUpload,
    removeFile,
    clearFiles,
  }
}
